import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import useImgFileReader from '../../../../../components/fileLoader/util/useImgFileReader';
import {MAX_VIDEO_SIZE} from '../../../../../constants';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IElementImage} from '../../../../../incase-domain/elements/image';
import {VideoSourceType} from '../../../../../incase-domain/elements/video';
import {uploadVideoFile} from '../../../../../service/files.service';
import useElementActions from '../../../../../utils/hooks/slideUpdates/useElementActions';
import {formatBytes, getUploadFileReader} from '../../../../../utils/utils';

export type DropHandlers = {
    onDragEnterOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
};

export function useSlideDropHandlers(): DropHandlers {
    const {t} = useTranslation();
    const {addNewElement} = useElementActions();

    const isSupportedImageType = useCallback((type: string) => {
        return type.toLowerCase().startsWith('image/');
    }, []);

    const isSupportedVideoType = useCallback((type: string) => {
        return type?.toLowerCase?.().startsWith('video/');
    }, []);

    const imgFilesOnLoad = useImgFileReader((imgs) => {
        if (!imgs || imgs.length === 0) return;
        if (imgs.length === 1) {
            const {imageSrc, height, widthHeightRatio, width} = imgs[0];
            addNewElement(ELEMENT.IMAGE, {imageSrc, width, height, widthHeightRatio});
        } else {
            const images = imgs.slice(0) as unknown as IElementImage[];
            addNewElement(ELEMENT.IMAGE_GALLERY, {images});
        }
    });

    const onDragEnterOver = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            const dt = e.dataTransfer;
            if (!dt) return;
            const items = dt.items ? Array.from(dt.items) : [];
            const hasSupportedFiles = items.some(
                (i) => i.kind === 'file' && (isSupportedImageType(i.type) || isSupportedVideoType(i.type))
            );
            if (hasSupportedFiles) {
                e.preventDefault();
                dt.dropEffect = 'copy';
            }
        },
        [isSupportedImageType, isSupportedVideoType]
    );

    const readImageFiles = (imageFiles: File[]): Promise<{reader: FileReader; file: File}[]> => {
        const readers = getUploadFileReader(imageFiles, 'dataurl') ?? [];

        const promises = readers.map(
            ({reader, file}) =>
                new Promise<{reader: FileReader; file: File}>((resolve) => {
                    reader.addEventListener('load', () => resolve({reader, file}), {once: true});
                })
        );

        return Promise.all(promises);
    };

    const onDrop = useCallback(
        async (e: React.DragEvent<HTMLDivElement>) => {
            const handleImageFiles = async (imageFiles: File[]): Promise<void> => {
                const results = await readImageFiles(imageFiles);
                imgFilesOnLoad(results);
            };

            const handleVideoFile = async (file: File): Promise<void> => {
                if (file.size > MAX_VIDEO_SIZE) {
                    toast.error(
                        `${t('presEditPage.editSidebar.validation.videoSize')}\n${t(
                            'presEditPage.editSidebar.validation.maxSize'
                        )} ${formatBytes(MAX_VIDEO_SIZE, 1, true)}`
                    );
                    return;
                }

                const {url} = await uploadVideoFile(file).catch(() => {
                    toast.error(t('common.uploadFailed'));
                    return {url: null};
                });
                if (url) addNewElement(ELEMENT.VIDEO, {videoSource: {src: url, type: VideoSourceType.url}});
            };

            const files = Array.from(e.dataTransfer?.files ?? []);
            const imageFiles = files.filter((f) => isSupportedImageType(f.type));
            const videoFiles = files.filter((f) => isSupportedVideoType(f.type));

            if (imageFiles.length === 0 && videoFiles.length === 0) return;

            e.preventDefault();

            if (imageFiles.length > 0) await handleImageFiles(imageFiles);
            await Promise.all(videoFiles.map(handleVideoFile));
        },
        [addNewElement, imgFilesOnLoad, isSupportedImageType, isSupportedVideoType, t]
    );

    return {onDragEnterOver, onDrop};
}

export default useSlideDropHandlers;
